import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { readFirst } from '@dg/shared-rxjs';

import { emitOnce } from '@ngneat/elf';
import {
  deleteAllEntities,
  resetActiveIds,
  upsertEntities,
} from '@ngneat/elf-entities';
import {
  deleteAllPages,
  selectCurrentPageEntities,
  setCurrentPage,
  setPage,
  updatePaginationData,
} from '@ngneat/elf-pagination';

import {
  initStoreState,
  PaginatedStore,
  PaginationProps,
} from '@dg/shared-rsm';

import { updateRequestStatus, getIsReady } from '@app/shared/rsm/utils';
import {
  initAssignmentsState,
  PAGINATION_LIMIT,
} from './utils/profile-assignments.utils';

import {
  AssignmentsState,
  AssignmentsResponse,
} from './profile-assignments.model';
import { NormalizedRecommendationEntity } from '@app/recommendations/recommendations.api';

const ASSIGNMENTS = 'assignments';

/**
 * Reactive store for "Assignments"
 */
@Injectable()
export class ProfileAssignmentsStore extends PaginatedStore<
  AssignmentsState,
  NormalizedRecommendationEntity
> {
  public state$: Observable<AssignmentsState>;

  private readonly assignments$: Observable<NormalizedRecommendationEntity[]>;

  constructor() {
    super(ASSIGNMENTS, initAssignmentsState);

    this.assignments$ = this._store.pipe<NormalizedRecommendationEntity[]>(
      selectCurrentPageEntities()
    );

    this.state$ = this._store.pipe(
      map((state) => {
        const results = readFirst<NormalizedRecommendationEntity[]>(
          this.assignments$
        );
        const pagination = readFirst<PaginationProps>(this.pagination$);

        // Unified store status for UI access;
        // Note:  aggregates pending, initializing, error (showSkeleton)
        const isReady = getIsReady(state);
        const isLoading = !isReady;

        return {
          ...state,
          isReady,
          isLoading,
          results,
          pagination,
        };
      }),
      shareReplay({ refCount: true })
    );
  }

  /**********************************************
   * Store Methods
   **********************************************/

  /**
   * When a new assignments update occurs and the store needs to be reset with full
   * pagination information. This should not be used to navigate pages nor
   * add more pages (retrieved with the same input criteria). But should be called when sorting, search term or the
   * assignment type changes
   */
  public updateAssignments(
    response: AssignmentsResponse,
    partialUpdates: Partial<AssignmentsState>,
    reset = false
  ) {
    const { assignments } = response;
    const pagination = {
      ...readFirst<PaginationProps>(this.pagination$),
      ...response.pagination,
      currentPage: response.pagination.currentPage,
      perPage: PAGINATION_LIMIT,
    };

    const updateState = (state: AssignmentsState): AssignmentsState => ({
      ...state,
      results: assignments,
      assignmentType: partialUpdates.assignmentType,
      term: partialUpdates.term,
      sort: partialUpdates.sort
    });

    emitOnce(() => {
      const initState = () => ({
        ...initAssignmentsState(),
        ...initStoreState(),
      });

      if (reset) {
        this._store.update(
          initState,
          resetActiveIds(),
          deleteAllEntities(),
          deleteAllPages()
        );
      }

      this._store.update(
        updateState,
        upsertEntities(assignments),
        updatePaginationData(pagination),
        setPage(
          pagination.currentPage,
          assignments.map((it) => it.id)
        ),
        resetActiveIds(),
        setCurrentPage(pagination.currentPage),
        updateRequestStatus(ASSIGNMENTS, 'success')
      );
    });
  }

  /**
   * Retain the search term on a failed search
   *
   * @param partialUpdate
   */
  public handleError(partialUpdate: Partial<AssignmentsState>) {
    const updateState = (state: AssignmentsState) => {
      const term = partialUpdate.term || state.term;
      return { ...state, term };
    };

    emitOnce(() => {
      this._store.update(
        updateState,
        updateRequestStatus(ASSIGNMENTS, 'error')
      );
    });
  }
}
